import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { PropertyFiltersType } from '@/types/marketplace';
import { cn } from '@/lib/utils';
import { useBranding } from '@/contexts/BrandingContext';
import { Search, Filter, Home, Building2, Map, LayoutGrid, X } from 'lucide-react';

interface PropertySidebarFiltersProps {
    filters: PropertyFiltersType;
    onFiltersChange: (filters: PropertyFiltersType) => void;
    resultCount: number;
    className?: string;
    onApply?: () => void;
}

export const PropertySidebarFilters = ({
    filters,
    onFiltersChange,
    resultCount,
    className,
    onApply
}: PropertySidebarFiltersProps) => {
    const { branding } = useBranding();
    const primaryColor = branding?.primary_color || '#0EA5E9';

    const updateFilter = (key: keyof PropertyFiltersType, value: string | string[] | null) => {
        onFiltersChange({
            ...filters,
            [key]: value
        });
    };

    const clearFilters = () => {
        onFiltersChange({
            location: "",
            type: "all",
            status: "all",
            priceMin: "",
            priceMax: "",
            bedrooms: null,
            bathrooms: null,
            parking: null,
            areaMin: "",
            areaMax: "",
            amenities: [],
            sortBy: "newest"
        });
    };

    const handleAmenityToggle = (amenity: string) => {
        const currentAmenities = filters.amenities || [];
        const newAmenities = currentAmenities.includes(amenity)
            ? currentAmenities.filter(a => a !== amenity)
            : [...currentAmenities, amenity];
        updateFilter('amenities', newAmenities);
    };

    const commonAmenities = [
        "Piscina", "Churrasqueira", "Academia", "Salão de Festas",
        "Portaria 24h", "Elevador", "Varanda Gourmet", "Playground"
    ];

    const renderPillSelector = (
        label: string,
        value: string | null,
        onChange: (val: string | null) => void,
        options: string[]
    ) => (
        <div className="space-y-3">
            <Label className="text-[13px] font-semibold text-foreground/80 uppercase tracking-wider">{label}</Label>
            <div className="flex flex-wrap gap-2">
                {options.map((opt) => (
                    <button
                        key={opt}
                        onClick={() => onChange(value === opt ? null : opt)}
                        className={cn(
                            "h-10 px-4 rounded-xl text-sm font-medium transition-all duration-200 border",
                            value === opt
                                ? "text-white border-transparent shadow-md transform scale-105"
                                : "bg-white text-muted-foreground border-border hover:border-foreground/20 hover:text-foreground hover:bg-muted/30"
                        )}
                        style={value === opt ? { backgroundColor: primaryColor } : {}}
                    >
                        {opt === "4+" ? "4+" : opt}
                    </button>
                ))}
            </div>
        </div>
    );

    const propertyTypes = [
        { id: 'all', label: 'Todos', icon: LayoutGrid },
        { id: 'house', label: 'Casa', icon: Home },
        { id: 'apartment', label: 'Apto', icon: Building2 },
        { id: 'land', label: 'Terreno', icon: Map },
    ];

    return (
        <div className={cn("bg-white rounded-[2rem] border border-border/60 shadow-[0_8px_40px_rgb(0,0,0,0.08)] p-5 sm:p-6 flex flex-col h-full", className)}>
            
            {/* Header */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-border/40">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: `${primaryColor}15` }}>
                        <Filter className="h-4 w-4" style={{ color: primaryColor }} />
                    </div>
                    <h2 className="font-bold text-lg text-foreground tracking-tight">Filtros</h2>
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-muted-foreground hover:text-destructive h-8 px-3 text-xs font-medium rounded-full hover:bg-destructive/10 transition-colors"
                >
                    Limpar
                </Button>
            </div>

            <div className="flex-1 overflow-y-auto pr-1 -mr-1 scrollbar-thin">
                <div className="space-y-7 pb-6">
                    
                    {/* Search */}
                    <div className="space-y-3">
                        <Label className="text-[13px] font-semibold text-foreground/80 uppercase tracking-wider">Localização</Label>
                        <div className="relative group">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-foreground transition-colors" style={filters.location ? {color: primaryColor} : {}} />
                            <Input
                                placeholder="Bairro, cidade ou rua..."
                                value={filters.location}
                                onChange={(e) => updateFilter('location', e.target.value)}
                                className="pl-10 h-12 text-sm bg-muted/30 border-border/50 focus:bg-white rounded-2xl transition-all shadow-sm focus:shadow-md"
                            />
                            {filters.location && (
                                <button onClick={() => updateFilter('location', '')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Status Toggle */}
                    <div className="bg-muted/40 p-1.5 rounded-2xl flex">
                        {['all', 'sale', 'rent'].map((status) => (
                            <button
                                key={status}
                                onClick={() => updateFilter('status', status)}
                                className={cn(
                                    "flex-1 h-10 rounded-xl text-sm font-semibold transition-all duration-300",
                                    filters.status === status
                                        ? "bg-white text-foreground shadow-sm scale-[1.02]"
                                        : "text-muted-foreground hover:text-foreground hover:bg-white/50"
                                )}
                            >
                                {status === 'all' ? 'Todos' : status === 'sale' ? 'Comprar' : 'Alugar'}
                            </button>
                        ))}
                    </div>

                    {/* Type Grid */}
                    <div className="space-y-3">
                        <Label className="text-[13px] font-semibold text-foreground/80 uppercase tracking-wider">Tipo de Imóvel</Label>
                        <div className="grid grid-cols-2 gap-2.5">
                            {propertyTypes.map((type) => {
                                const Icon = type.icon;
                                const isSelected = filters.type === type.id;
                                return (
                                    <button
                                        key={type.id}
                                        onClick={() => updateFilter('type', type.id)}
                                        className={cn(
                                            "flex flex-col items-center justify-center p-3 rounded-2xl transition-all duration-200 border",
                                            isSelected
                                                ? "border-transparent shadow-md scale-[1.02]"
                                                : "bg-white border-border/50 text-muted-foreground hover:border-foreground/20 hover:bg-muted/30 hover:text-foreground"
                                        )}
                                        style={isSelected ? { backgroundColor: primaryColor, color: 'white' } : {}}
                                    >
                                        <Icon className="h-6 w-6 mb-2 opacity-90" />
                                        <span className="text-xs font-semibold">{type.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Price Range */}
                    <div className="space-y-3">
                        <Label className="text-[13px] font-semibold text-foreground/80 uppercase tracking-wider">Faixa de Preço</Label>
                        <div className="flex items-center gap-2">
                            <div className="relative flex-1 group">
                                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-muted-foreground group-focus-within:text-foreground">R$</span>
                                <Input
                                    type="number"
                                    placeholder="Mínimo"
                                    value={filters.priceMin}
                                    onChange={(e) => updateFilter('priceMin', e.target.value)}
                                    className="pl-9 h-11 text-sm bg-white border-border/50 focus:bg-white rounded-xl transition-all shadow-sm"
                                />
                            </div>
                            <span className="text-muted-foreground/50">-</span>
                            <div className="relative flex-1 group">
                                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-muted-foreground group-focus-within:text-foreground">R$</span>
                                <Input
                                    type="number"
                                    placeholder="Máximo"
                                    value={filters.priceMax}
                                    onChange={(e) => updateFilter('priceMax', e.target.value)}
                                    className="pl-9 h-11 text-sm bg-white border-border/50 focus:bg-white rounded-xl transition-all shadow-sm"
                                />
                            </div>
                        </div>
                    </div>

                    <Accordion type="multiple" defaultValue={["rooms"]} className="w-full space-y-4">
                        {/* Rooms */}
                        <AccordionItem value="rooms" className="border-none bg-muted/20 rounded-2xl px-4">
                            <AccordionTrigger className="py-4 hover:no-underline text-sm font-bold text-foreground">
                                Características
                            </AccordionTrigger>
                            <AccordionContent className="pb-4">
                                <div className="space-y-6 pt-2">
                                    {renderPillSelector("Quartos", filters.bedrooms, (val) => updateFilter('bedrooms', val), ["1", "2", "3", "4+"])}
                                    {renderPillSelector("Banheiros", filters.bathrooms, (val) => updateFilter('bathrooms', val), ["1", "2", "3", "4+"])}
                                    {renderPillSelector("Vagas", filters.parking, (val) => updateFilter('parking', val), ["1", "2", "3", "4+"])}
                                </div>
                            </AccordionContent>
                        </AccordionItem>

                        {/* Area */}
                        <AccordionItem value="area" className="border-none bg-muted/20 rounded-2xl px-4">
                            <AccordionTrigger className="py-4 hover:no-underline text-sm font-bold text-foreground">
                                Área (m²)
                            </AccordionTrigger>
                            <AccordionContent className="pb-4">
                                <div className="flex items-center gap-2 pt-2">
                                    <Input
                                        type="number"
                                        placeholder="Mínimo"
                                        value={filters.areaMin}
                                        onChange={(e) => updateFilter('areaMin', e.target.value)}
                                        className="h-11 text-sm bg-white border-none rounded-xl shadow-sm"
                                    />
                                    <span className="text-muted-foreground/50">-</span>
                                    <Input
                                        type="number"
                                        placeholder="Máximo"
                                        value={filters.areaMax}
                                        onChange={(e) => updateFilter('areaMax', e.target.value)}
                                        className="h-11 text-sm bg-white border-none rounded-xl shadow-sm"
                                    />
                                </div>
                            </AccordionContent>
                        </AccordionItem>

                        {/* Amenities */}
                        <AccordionItem value="amenities" className="border-none bg-muted/20 rounded-2xl px-4">
                            <AccordionTrigger className="py-4 hover:no-underline text-sm font-bold text-foreground">
                                Comodidades
                            </AccordionTrigger>
                            <AccordionContent className="pb-4">
                                <div className="grid grid-cols-1 gap-3 pt-2">
                                    {commonAmenities.map((amenity) => (
                                        <div key={amenity} className="flex items-center space-x-3 group cursor-pointer" onClick={() => handleAmenityToggle(amenity)}>
                                            <div className={cn(
                                                "w-5 h-5 rounded-md border flex items-center justify-center transition-all",
                                                (filters.amenities || []).includes(amenity) 
                                                    ? "border-transparent" 
                                                    : "border-border/80 bg-white group-hover:border-foreground/30"
                                            )}
                                            style={(filters.amenities || []).includes(amenity) ? { backgroundColor: primaryColor } : {}}
                                            >
                                                {(filters.amenities || []).includes(amenity) && (
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                                )}
                                            </div>
                                            <label className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors cursor-pointer select-none">
                                                {amenity}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>
            </div>

            {/* Footer / CTA */}
            <div className="pt-5 mt-auto border-t border-border/40">
                <Button 
                    onClick={onApply}
                    className="w-full font-bold h-12 text-sm text-white rounded-xl shadow-lg transition-transform hover:scale-[1.02] active:scale-95" 
                    style={{ backgroundColor: primaryColor, boxShadow: `0 10px 25px -5px ${primaryColor}66` }}
                >
                    Ver {resultCount} {resultCount === 1 ? 'imóvel' : 'imóveis'}
                </Button>
            </div>
            
            <style>{`
                .scrollbar-thin::-webkit-scrollbar {
                    width: 4px;
                }
                .scrollbar-thin::-webkit-scrollbar-track {
                    background: transparent;
                }
                .scrollbar-thin::-webkit-scrollbar-thumb {
                    background: #e5e7eb;
                    border-radius: 4px;
                }
                .scrollbar-thin:hover::-webkit-scrollbar-thumb {
                    background: #d1d5db;
                }
            `}</style>
        </div>
    );
};
